import * as React from 'react'
import withHydrationOnDemand from 'react-hydration-on-demand'

import { ProductClearance } from '@thg-commerce/enterprise-components'
import {
  i18n,
  LIST_EXTENSION,
  useEnterpriseContext,
  useFormattableI18nProperty,
  useSiteConfig,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import {
  productEventsCategory,
  pushToEventGA4,
} from '@thg-commerce/enterprise-metrics/src/data_layer/pushToDataLayer/utils'
import { BrandLogo, BrandSize } from '@thg-commerce/gravity-elements'
import { List } from '@thg-commerce/gravity-patterns'
import { spacing, styled, Text } from '@thg-commerce/gravity-theme'

import {
  BuyNowPayLaterContainer,
  ExternalIdentifierDisplay,
} from '../../components'
import { useProductPage } from '../../context/'

import { RecommendationItem } from './RecommendationItem'

const RecommendationItemComponent = withHydrationOnDemand({
  on: [['visible']],
})(RecommendationItem)

const RecommendationTitle = styled.p`
  ${Text('medium1', 'alternate')};
  margin-top: ${spacing(1)};
`

const ProductTitle = styled.h1`
  ${Text('large2', 'default')};
`

const getBrandUrl = (brand, brandUrl, originUrl) => {
  if (brand.page?.path) {
    return `${brand.page.path}${LIST_EXTENSION}`
  }
  return brandUrl([
    originUrl,
    `${brand.name
      .replace(/\s/g, '-')
      .replace('.', '')
      .toLowerCase()}${LIST_EXTENSION}`,
  ])
}

const getBrandLogoProps = (
  brand,
  showBrandLogoOnProductPage,
  enableProductBrandPageLink,
  label,
  originUrl,
  brandUrl,
) =>
  brand?.imageUrl &&
  brand?.name &&
  showBrandLogoOnProductPage && {
    brandLabel: label,
    imageUrl: brand.imageUrl,
    brandSize: BrandSize.LARGE,
    ...(enableProductBrandPageLink
      ? {
          brandUrl: getBrandUrl(brand, brandUrl, originUrl),
        }
      : {}),
  }

export const RecommendationItemListRenderer = () => {
  const {
    product,
    setSelectedVariant,
    selectedOptions,
    selectedVariant,
    setSelectedOptions,
    manualRecommendations,
    storeStockActive,
    setStoreStockActive,
  } = useProductPage()

  const { originUrl } = useSiteDefinition()
  const { currency } = useEnterpriseContext()
  const {
    enableProductBrandPageLink,
    showFlagsOnProduct,
    useExternalIdentifier,
    useGA4EnhancedEcom,
    alternateProductKeysForCategories,
    showBrandLogoOnProductPage,
  } = useSiteConfig()

  React.useEffect(() => {
    if (useGA4EnhancedEcom) {
      pushToEventGA4({
        event: 'ecom_event',
        event_name: 'view_item_list',
        ecommerce: {
          currencyCode: currency,
          items: manualRecommendations?.map((item, index) => ({
            index,
            ...productEventsCategory(
              item.content,
              alternateProductKeysForCategories,
            ),
            item_brand: item.brand?.name,
            item_id: useExternalIdentifier
              ? product?.externalIdentifier
              : product?.sku,
            item_list_name: 'Style With',
            item_name: item.title,
            item_variant: useExternalIdentifier
              ? item.defaultVariant.externalIdentifier
              : item.sku,
            price: item.defaultVariant?.price?.price.amount,
            quantity: 1,
          })),
        },
      })
    }
  }, [
    currency,
    manualRecommendations,
    product?.externalIdentifier,
    product?.sku,
    useExternalIdentifier,
    useGA4EnhancedEcom,
    alternateProductKeysForCategories,
  ])

  const i18nText = {
    reviewsStarsLabel: i18n('reviews.stars.label'),
    reviewsLabel: i18n('reviews.reviews.label'),
    addToBasketText: i18n('product.optionselector.addtobasket.text'),
    rrpText: i18n('product.price.rrp.text'),
    title: i18n('product.recommend.manual.title'),
    closeI18nText: {
      closeAriaLabel: i18n('general.modal.close.button.arialabel'),
      closeLabel: i18n('general.modal.close.button.label'),
    },
    freeGiftMessage: i18n('product.marketedspecialoffer.freegift.text'),
    brandLogoLabel: useFormattableI18nProperty('product.brand.logo.label'),
    brandUrl: useFormattableI18nProperty('product.brand.logo.url'),
  }

  if (!product || !manualRecommendations) {
    return null
  }
  const getURLwithTrackingParam = (prodURL: string): string => {
    const url = new URL(prodURL, originUrl)
    url.searchParams.append('rctxt', 'manual')
    return url.href
  }

  const brandLogoProps = getBrandLogoProps(
    product.brand,
    showBrandLogoOnProductPage,
    enableProductBrandPageLink,
    i18nText.brandLogoLabel(product.brand?.name),
    originUrl,
    i18nText.brandUrl,
  )

  return (
    <div
      data-testid="recommendation-item-list-container"
      style={{ marginTop: spacing(2) }}
    >
      {brandLogoProps && <BrandLogo {...brandLogoProps} />}
      <ProductTitle data-testid="recommendation-item-title">
        {selectedVariant.title || product.title}
      </ProductTitle>
      <ExternalIdentifierDisplay
        product={product}
        selectedVariant={selectedVariant}
      />
      {showFlagsOnProduct && <ProductClearance content={product?.content} />}
      <List useAlternateTitleStyle={true}>
        <li
          style={{
            marginTop: '0',
            display: 'flex',
            flexDirection: 'column',
            gap: spacing(3),
          }}
        >
          <RecommendationItem
            product={product}
            selectedVariant={selectedVariant}
            variantChangedCallback={setSelectedVariant}
            selectedOptions={selectedOptions}
            optionsChangedCallback={setSelectedOptions}
            content={product.content}
            isFirstRecommendation={true}
            marketedSpecialOffer={
              product.marketedSpecialOffer && {
                title: product.marketedSpecialOffer.title,
                i18nText: {
                  closeI18nText: i18nText.closeI18nText,
                  freeGiftMessage: i18nText.freeGiftMessage,
                },
              }
            }
            storeStockActive={storeStockActive}
            setStoreStockActive={setStoreStockActive}
          />
          <BuyNowPayLaterContainer />
        </li>
        {manualRecommendations.map((recommendationProduct, index) => {
          if (index === 0) {
            return (
              <li
                data-testid="recommendation-list-item-0"
                style={{
                  marginTop: '0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing(3),
                }}
              >
                <RecommendationTitle data-testid="recommendation-item-list-title">
                  {i18nText.title}
                </RecommendationTitle>
                <RecommendationItem
                  key={index}
                  url={getURLwithTrackingParam(recommendationProduct.url)}
                  product={recommendationProduct}
                  options={recommendationProduct.options}
                  isFirstRecommendation={false}
                />
              </li>
            )
          }
          return (
            <li
              data-testid={`recommendation-list-item-${index}`}
              style={{ marginTop: '0' }}
            >
              <RecommendationItemComponent
                key={index}
                url={getURLwithTrackingParam(recommendationProduct.url)}
                product={recommendationProduct}
                options={recommendationProduct.options}
                isFirstRecommendation={false}
              />
            </li>
          )
        })}
      </List>
    </div>
  )
}
